import { pagesApi, type FullPreviewData, type Page } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { openPreview } from '@/utils/desktopActions';

/**
 * What a preview needs. The editor passes its live (unsaved) form state; the
 * Pages list passes a stored row, which may not carry `content` — pass the `id`
 * and it gets fetched.
 */
export type PreviewablePage = Pick<Page, 'title'> &
  Partial<Pick<Page, 'id' | 'slug' | 'content' | 'editor_mode' | 'custom_css' | 'custom_js' | 'show_title' | 'template'>>;

/**
 * Open a page preview — the one implementation shared by the page editor's
 * Preview button and the Pages list row, so the two can't drift.
 * AB#488/#530 (browser) + AB#533 (desktop): openPreview branches on isTauri.
 */
export async function openPagePreview(siteSlug: string, page: PreviewablePage): Promise<void> {
  try {
    const source =
      page.content === undefined && page.id
        ? (await pagesApi.get(siteSlug, page.id)).data ?? page
        : page;

    const previewData: FullPreviewData = {
      title: source.title,
      content: source.content ?? '',
      slug: source.slug || 'preview',
      editor_mode: source.editor_mode,
      custom_css: source.custom_css || undefined,
      custom_js: source.custom_js || undefined,
      show_title: source.show_title ?? true,
      ...(source.template ? { template: source.template } : {}),
    };

    const previewKey = `preview-${Date.now()}`;
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    await openPreview({
      browserUrl: `${base}/sites/${siteSlug}/preview?key=${previewKey}`,
      storageKey: previewKey,
      storageValue: JSON.stringify(previewData),
      render: () => pagesApi.previewFull(siteSlug, previewData),
    });
  } catch (err) {
    toast.error(err instanceof Error ? err.message : 'Failed to open preview');
  }
}
